import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OrderService, SalesIdeas } from '../../../_services/order.service';
import { statusLabel, statusTone } from '../order-activity/order-activity.component';

export type LeadPanel = 'lost' | 'samples' | 'carts';

const COLLAPSED_KEY = 'salesIdeas.collapsed';

// "Ideas for this hour" at the top of the Sales page: who to call next, what
// to pitch, and how today is going. It never gets in the way of the orders
// list - it loads after it, hides itself if it can't load, and folds down to
// one line (remembered per browser). Refreshes itself on the hour; the call
// list also refreshes when the agent comes back to the tab.
@Component({
    selector: 'app-sales-ideas',
    imports: [CommonModule, MatIconModule, MatTooltipModule],
    templateUrl: './sales-ideas.component.html'
})
export class SalesIdeasComponent implements OnInit, OnDestroy {
  // Opens one of the Sales page's follow-up panels.
  @Output() openLeads = new EventEmitter<LeadPanel>();
  // Opens today's orders with a status ('ALL' for every one).
  @Output() openOrders = new EventEmitter<string>();

  readonly statusTone = statusTone;
  readonly statusLabel = statusLabel;

  get todayOrderCount(): number {
    return (this.ideas?.statusCounts ?? []).reduce((s, t) => s + t.count, 0);
  }

  ideas: SalesIdeas | null = null;
  failed = false;
  loading = false;
  collapsed = false;
  private hourTimer: any;
  private loadedAt = 0;
  // Back on the tab after a while: the call list may have moved on.
  private onVisible = () => {
    if (document.visibilityState === 'visible' && Date.now() - this.loadedAt > 60_000) this.load();
  };

  constructor(
    private orderService: OrderService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    try {
      this.collapsed = localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      this.collapsed = false;
    }
    // After the orders list has had its turn.
    setTimeout(() => this.load(), 400);
    this.scheduleNextHour();
    document.addEventListener('visibilitychange', this.onVisible);
  }

  ngOnDestroy(): void {
    clearTimeout(this.hourTimer);
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  // Reload just after each new hour starts, when the server has fresh ideas.
  private scheduleNextHour(): void {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1, 0, 30);
    this.hourTimer = setTimeout(() => {
      this.load();
      this.scheduleNextHour();
    }, next.getTime() - now.getTime());
  }

  load(): void {
    if (this.loading) return;
    this.loading = true;
    this.orderService.getSalesIdeas().subscribe({
      next: (r) => {
        this.ideas = r;
        this.loadedAt = Date.now();
        this.failed = false;
        this.loading = false;
      },
      error: () => {
        // Keep showing the last ideas if we had some; otherwise stay out of the way.
        this.failed = !this.ideas;
        this.loading = false;
      },
    });
  }

  toggle(): void {
    this.collapsed = !this.collapsed;
    try {
      localStorage.setItem(COLLAPSED_KEY, this.collapsed ? '1' : '0');
    } catch {
      // per-browser nicety only
    }
  }

  // ---- actions ----
  openLead(kind: 'cart' | 'pending' | 'sample'): void {
    this.openLeads.emit(kind === 'cart' ? 'carts' : kind === 'pending' ? 'lost' : 'samples');
  }

  startSale(productSlug: string): void {
    this.router.navigate(['/sales/create'], { state: { pitchProductSlug: productSlug } });
  }

  // ---- display ----
  get summary(): string {
    if (!this.ideas) return '';
    const bits: string[] = [];
    const calls = this.ideas.callNext.length;
    if (calls) bits.push(`${calls} ${calls === 1 ? 'person' : 'people'} to call`);
    if (this.ideas.pitch.length) bits.push(`${this.ideas.pitch.length} products to pitch`);
    if (this.ideas.tip?.kind === 'busy-now') bits.push('busy hour now');
    else if (this.ideas.tip?.kind === 'busy-soon') bits.push('busy time ahead');
    return bits.join(' · ');
  }

  // Today's orders against a usual day like today, capped for the bar.
  get pacePct(): number {
    const p = this.ideas?.pace;
    if (!p || p.usualOrders <= 0) return p?.orders ? 100 : 0;
    return Math.min((p.orders / p.usualOrders) * 100, 100);
  }

  get paceAhead(): boolean {
    const p = this.ideas?.pace;
    return !!p && p.orders >= p.usualOrders;
  }

  leadIcon(kind: string): string {
    return kind === 'cart' ? 'remove_shopping_cart' : kind === 'pending' ? 'hourglass_top' : 'inventory_2';
  }

  leadLabel(kind: string): string {
    return kind === 'cart' ? 'Abandoned cart' : kind === 'pending' ? 'Unpaid order' : 'Sample lead';
  }

  pitchIcon(kind: string): string {
    return { rising: 'trending_up', viewed: 'visibility', trending: 'local_fire_department', offer: 'sell', stock: 'inventory_2', season: 'wb_sunny' }[kind] ?? 'lightbulb';
  }

  tipClass(kind: string | undefined): string {
    switch (kind) {
      case 'busy-now':
        return 'bg-amber-50 border-amber-200 text-amber-900';
      case 'busy-soon':
        return 'bg-orange-50 border-orange-200 text-orange-900';
      case 'quiet':
        return 'bg-sky-50 border-sky-200 text-sky-900';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  }

  tipIcon(kind: string | undefined): string {
    return kind === 'busy-now' || kind === 'busy-soon' ? 'trending_up' : kind === 'quiet' ? 'self_improvement' : 'schedule';
  }

  telLink(phone: string): string {
    return 'tel:' + phone.replace(/[^\d+]/g, '');
  }
}
